import { useMemo, useState } from 'react';
import { type Segment } from '@wa/shared';

/**
 * The left column: every broadcast list, the ways to fill one, and the way to
 * make one.
 *
 * Matched to the reference row for row. Its list is a *list of audiences* — the
 * name and how many people are in it — and nothing else, because the only thing
 * anybody does here is pick one. Rule-based lists show "View member count" in
 * place of a number: counting one is a query, and the reference makes the same
 * distinction with a lightning bolt beside the name.
 */
type Sort = 'created-desc' | 'created-asc' | 'name' | 'members';

const SORT_LABEL: Record<Sort, string> = {
  'created-desc': 'Date created - new to old',
  'created-asc': 'Date created - old to new',
  'name': 'Name A to Z',
  'members': 'Members - most first',
};

export function ListsColumn({
  segments, selectedId, onSelect, onNewList, onExcelImport, onSampleFile, onQuickImport, isLoading,
}: {
  segments: Segment[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onNewList: () => void;
  onExcelImport: () => void;
  onSampleFile: () => void;
  onQuickImport: () => void;
  isLoading: boolean;
}) {
  const [sort, setSort] = useState<Sort>('created-desc');
  const [searching, setSearching] = useState(false);
  const [q, setQ] = useState('');

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = needle ? segments.filter((s) => s.name.toLowerCase().includes(needle)) : [...segments];
    const by: Record<Sort, (a: Segment, b: Segment) => number> = {
      'created-desc': (a, b) => b.createdAt.localeCompare(a.createdAt),
      'created-asc': (a, b) => a.createdAt.localeCompare(b.createdAt),
      'name': (a, b) => a.name.localeCompare(b.name),
      'members': (a, b) => (b.memberCount ?? -1) - (a.memberCount ?? -1),
    };
    return list.sort(by[sort]);
  }, [segments, q, sort]);

  return (
    <section className="flex h-full min-h-0 flex-col border-r border-(--color-line) bg-(--color-surface)">
      {/* Header: the title, or the search box in its place. The reference swaps
          one for the other rather than stacking them, which keeps the column the
          same height whether or not somebody is searching. */}
      <header className="flex h-14 shrink-0 items-center gap-1 px-4">
        {searching ? (
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onBlur={() => { if (!q) setSearching(false); }}
            placeholder="Search lists"
            aria-label="Search lists"
            className="min-w-0 flex-1 bg-transparent text-body outline-none placeholder:text-(--color-faint)"
          />
        ) : (
          <h2 className="min-w-0 flex-1 truncate text-lead font-semibold">Broadcast lists</h2>
        )}
        <button
          type="button"
          onClick={() => { setSearching((v) => !v); if (searching) setQ(''); }}
          aria-label={searching ? 'Close search' : 'Search lists'}
          className="tap grid size-9 place-items-center rounded-full text-(--color-ink-soft) hover:bg-(--color-sunken)"
        >
          {searching ? <IconClose /> : <IconSearch />}
        </button>
      </header>

      {/* Sort. A select styled as the reference's row: a sort glyph, the label, a
          chevron. A native <select> underneath, because a custom menu for four
          options is a component nobody asked for. */}
      <label className="relative flex h-8 shrink-0 items-center gap-2 border-y border-(--color-line-dim) bg-(--color-paper) px-4 text-small text-(--color-ink-soft)">
        <IconSort />
        <span className="min-w-0 flex-1 truncate">Sort by <b className="font-medium text-(--color-ink)">{SORT_LABEL[sort]}</b></span>
        <IconChevron />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as Sort)}
          aria-label="Sort lists"
          className="absolute inset-0 cursor-pointer opacity-0"
        >
          {(Object.keys(SORT_LABEL) as Sort[]).map((k) => <option key={k} value={k}>{SORT_LABEL[k]}</option>)}
        </select>
      </label>

      {/* Three ways to fill a list, as tiles. The first two share a box and the
          third stands alone, exactly as the reference draws them. */}
      <div className="grid shrink-0 grid-cols-[2fr_1fr] gap-2 p-3">
        <div className="grid grid-cols-2 divide-x divide-(--color-line) rounded-(--radius-field) border border-(--color-line)">
          <Tile label="Excel Import" onClick={onExcelImport} icon={<IconExcel />} />
          <Tile label="Sample File" onClick={onSampleFile} icon={<IconFile />} />
        </div>
        <div className="rounded-(--radius-field) border border-(--color-line)">
          <Tile label="Quick Import" onClick={onQuickImport} icon={<IconBolt />} />
        </div>
      </div>

      <ul className="pane-scroll min-h-0 flex-1 overflow-y-auto">
        {isLoading && <li className="px-4 py-6 text-center text-small text-(--color-muted)">Loading…</li>}
        {!isLoading && shown.length === 0 && (
          <li className="px-6 py-10 text-center text-small text-(--color-muted)">
            {q ? `No list matches “${q.trim()}”.` : 'No lists yet. Make one, then import people into it.'}
          </li>
        )}
        {shown.map((s) => {
          const selected = s.id === selectedId;
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onSelect(s.id)}
                aria-current={selected ? 'true' : undefined}
                className={[
                  'flex w-full items-center gap-2 border-b border-(--color-line-dim) px-4 py-2.5 text-left transition-colors',
                  selected ? 'bg-(--color-brand-wash)' : 'hover:bg-(--color-sunken)',
                ].join(' ')}
              >
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate text-body font-semibold text-(--color-ink)">{s.name}</span>
                    {s.kind === 1 && <span className="shrink-0 text-(--color-warn)" title="Rule-based list"><IconBolt small /></span>}
                  </span>
                  <span className="block text-small text-(--color-muted)">
                    {s.kind === 1
                      ? <span className="text-(--color-brand)">View member count</span>
                      : `${(s.memberCount ?? 0).toLocaleString('en-IN')} ${s.memberCount === 1 ? 'Member' : 'Members'}`}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <footer className="flex shrink-0 justify-end p-3">
        <button
          type="button"
          onClick={onNewList}
          className="tap flex items-center gap-2 rounded-(--radius-field) bg-(--color-brand) px-4 py-2 text-body font-semibold text-(--color-brand-ink) shadow-(--shadow-pop) hover:bg-(--color-brand-strong)"
        >
          <IconPlus /> New List
        </button>
      </footer>
    </section>
  );
}

function Tile({ label, icon, onClick }: { label: string; icon: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="tap flex h-16 w-full flex-col items-center justify-center gap-1.5 rounded-(--radius-field) text-small font-medium text-(--color-ink-soft) hover:bg-(--color-sunken)"
    >
      <span aria-hidden="true">{icon}</span>
      {label}
    </button>
  );
}

const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
const IconSearch = () => <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><circle cx="8.8" cy="8.8" r="5.2" {...stroke} /><path d="m12.8 12.8 3.6 3.6" {...stroke} /></svg>;
const IconClose = () => <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" {...stroke} /></svg>;
const IconSort = () => <svg viewBox="0 0 20 20" className="size-4 shrink-0" aria-hidden="true"><path d="M6 4v12M6 16l-2.5-2.5M6 16l2.5-2.5M14 16V4M14 4l-2.5 2.5M14 4l2.5 2.5" {...stroke} /></svg>;
const IconChevron = () => <svg viewBox="0 0 20 20" className="size-4 shrink-0" aria-hidden="true"><path d="m6 8 4 4 4-4" {...stroke} /></svg>;
const IconPlus = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M10 4v12M4 10h12" {...stroke} strokeWidth={2} /></svg>;
const IconFile = () => <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="M5 3h7l4 4v10H5z" {...stroke} /><path d="M12 3v4h4" {...stroke} /></svg>;
const IconExcel = () => (
  <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true">
    <path d="M7 3h7l3 3v11H7z" {...stroke} />
    <rect x="3" y="6" width="9" height="9" rx="1" fill="#1D6F42" />
    <path d="m5.6 8.4 3.8 4.2M9.4 8.4l-3.8 4.2" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
export const IconBolt = ({ small = false }: { small?: boolean }) => (
  <svg viewBox="0 0 20 20" className={small ? 'size-3.5' : 'size-5'} aria-hidden="true">
    <path d="M11.5 2.5 5 11h4.2L8.5 17.5 15 9h-4.2z" fill="#E0A100" stroke="#B57F00" strokeWidth="0.8" strokeLinejoin="round" />
  </svg>
);
